import { useMemo, useRef, useState } from 'react';

import { cva } from 'class-variance-authority';
import { format, parse } from 'date-fns';
import { match } from 'ts-pattern';

import { cn } from '@/lib/utils';

import CloseSmallIcon from '../icons/close-small';

export type DatePickerType = 'date' | 'time' | 'datetime-local';

export interface NativeDatePickerProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange'> {
  error?: boolean;
  type?: DatePickerType;
  value?: Date | null;
  onChange?: (date: Date | null) => void;
  min?: string;
  max?: string;
}

const datePickerVariants = cva(
  'bg-white flex h-[41px] w-full rounded border-2 border-grey-3 p-2 text-sm placeholder:text-grey-5 disabled:bg-grey-1 focus-visible:border-default-blue focus-visible:outline-none w-full appearance-none text-sm [&::-webkit-date-and-time-value]:text-left [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none',
  {
    variants: {
      error: {
        true: 'border-default-red',
      },
      hasValue: {
        false:
          'text-transparent [&::-webkit-datetime-edit-day-field]:text-transparent [&::-webkit-datetime-edit-month-field]:text-transparent [&::-webkit-datetime-edit-year-field]:text-transparent',
      },
    },
  },
);

const NativeDatePicker = ({ className, error, type = 'date', value, onChange, placeholder, disabled, ...props }: NativeDatePickerProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  // Convert Date object to string format required by native input
  function formatDateForInput(date: Date | null | undefined): string {
    if (!date) return '';

    return match(type)
      .with('date', () => format(date, 'yyyy-MM-dd'))
      .with('time', () => format(date, 'HH:mm'))
      .with('datetime-local', () => format(date, "yyyy-MM-dd'T'HH:mm"))
      .otherwise(() => '');
  }

  // Handle change event from native input
  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const inputValue = e.target.value;

    if (!inputValue) {
      // If input is cleared, return null
      onChange?.(null);
      return;
    }

    try {
      const newDate = match(type)
        .with('date', () => {
          return parse(inputValue, 'yyyy-MM-dd', new Date());
        })
        .with('time', () => {
          const now = new Date();
          const timeDate = parse(inputValue, 'HH:mm', now);
          // Only take the time portion from the parsed date
          now.setHours(timeDate.getHours(), timeDate.getMinutes(), 0, 0);
          return now;
        })
        .with('datetime-local', () => {
          return parse(inputValue, "yyyy-MM-dd'T'HH:mm", new Date());
        })
        .otherwise(() => new Date(inputValue));

      // Only call onChange if date is valid and within min/max constraints
      if (!isNaN(newDate.getTime())) {
        const minDate = props.min ? parse(props.min, 'yyyy-MM-dd', new Date()) : null;
        const maxDate = props.max ? parse(props.max, 'yyyy-MM-dd', new Date()) : null;

        // Compare dates using date-fns format to ignore time
        const compareDate = format(newDate, 'yyyy-MM-dd');
        const compareMin = minDate ? format(minDate, 'yyyy-MM-dd') : null;
        const compareMax = maxDate ? format(maxDate, 'yyyy-MM-dd') : null;

        // Special case: if min and max are the same date, allow exact matches
        const isExactMatch = compareMin && compareMax && compareMin === compareMax && compareDate === compareMin;

        if (isExactMatch || ((!minDate || newDate >= minDate) && (!maxDate || newDate <= maxDate))) {
          onChange?.(newDate);
        }
      }
    } catch (err) {
      console.error('Error parsing date:', err);
    }
  }

  // Handle focus event
  function handleFocus(e: React.FocusEvent<HTMLInputElement>) {
    setIsFocused(true);

    // Call any provided onFocus handler
    props.onFocus?.(e);
  }

  // Handle blur event
  function handleBlur(e: React.FocusEvent<HTMLInputElement>) {
    setIsFocused(false);

    // Call any provided onBlur handler
    props.onBlur?.(e);
  }

  // Function to show the native date picker
  function showDatePicker() {
    if (inputRef.current && !disabled) {
      try {
        // Use showPicker method from HTMLInputElement
        // https://developer.mozilla.org/en-US/docs/Web/API/HTMLInputElement/showPicker
        if (typeof inputRef.current.showPicker === 'function') {
          inputRef.current.showPicker();
        } else {
          // Fallback for browsers without showPicker support
          inputRef.current.focus();
        }
      } catch (err) {
        console.error('Error showing picker:', err);
        inputRef.current.focus();
      }
    }
  }

  const hasValue = useMemo(() => !!value, [value]);
  const defaultPlaceholder = useMemo(() => `Select ${type === 'time' ? 'time' : type === 'datetime-local' ? 'date and time' : 'date'}`, [type]);

  return (
    <div className={cn('relative', !disabled && 'cursor-pointer')} onClick={showDatePicker}>
      {/* The actual input */}
      <input
        ref={inputRef}
        type={type}
        className={cn(
          datePickerVariants({
            error,
            hasValue: hasValue || isFocused,
          }),
          className,
        )}
        value={formatDateForInput(value)}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        disabled={disabled}
        {...props}
      />

      {!disabled && hasValue && (
        <button
          type="button"
          aria-label="Clear"
          onClick={(event) => {
            event.stopPropagation();
            onChange?.(null);
          }}
          className="text-grey-5 hover:text-default-text absolute inset-y-0 right-2 flex items-center">
          <CloseSmallIcon />
        </button>
      )}

      {/* Placeholder overlay - shown when no value is selected and not focused */}
      {!hasValue && !isFocused && (
        <div className="pointer-events-none absolute inset-0 flex items-center px-2">
          <span className="text-grey-5 text-sm">{placeholder || defaultPlaceholder}</span>
        </div>
      )}
    </div>
  );
};

NativeDatePicker.displayName = 'NativeDatePicker';

export { NativeDatePicker, datePickerVariants };
